<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue'

import AboutHeader from './Header.vue'
import AboutHero from './Hero.vue'
import AboutHeroB from './HeroB.vue'
import AboutMain from './Main.vue'
import FooterLP from '@/components/_core/FooterLP.vue'
import MediaQueryObserver from '@/components/_core/MediaQueryObserver.vue'
import SplitTestWrapper from '@/components/marketing/SplitTestWrapper.vue'
import { useHeaderStore } from '@/stores/header'
import { useScreenLockStore } from '@/stores/screenLock'
import { useUserStore } from '@/stores/user'
import { useClientStore } from '@/stores/client'
import { UrlUtil } from '@/utilities/url'

export default defineComponent({
  name: 'AboutApp',

  components: {
    AboutHeader,
    AboutHero,
    AboutHeroB,
    AboutMain,
    FooterLP,
    MediaQueryObserver,
    SplitTestWrapper,
  },

  props: {
    hasHeader: {
      type: Boolean,
      default: true,
    },

    hasFooter: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    // constants
    const QUERY_KEY_PATTERNS = ['mail_campaign', 'media']

    // store
    const headerStore = useHeaderStore()
    const screenLockStore = useScreenLockStore()
    const userStore = useUserStore()
    const clientStore = useClientStore()

    const sections = ref<string[]>([])
    const query = ref('')

    const url = new UrlUtil()

    const ctaButtonLabel = computed(() => {
      switch (query.value) {
        case 'mail_campaign':
          return '無料登録して、事例ニュースレターを受け取る'
        case 'media':
          return '無料登録して、記事を読む'
        default:
          return '<span class="span u-media__gtPC" data-model="inline">無料登録して、</span>経営課題の解決事例を受け取る'
      }
    })

    const trackingCodePrefix = computed(() => {
      const TRACKING_CODE_PREFIX = `${clientStore.trackingCodePrefix}_campaign_about_bizhint`
      return query.value
        ? `${TRACKING_CODE_PREFIX}_${query.value}`
        : TRACKING_CODE_PREFIX
    })

    onMounted(() => {
      clientStore.setIsTouchDevice()

      query.value = url.hasQuery(QUERY_KEY_PATTERNS[0])
        ? QUERY_KEY_PATTERNS[0]
        : url.hasQuery(QUERY_KEY_PATTERNS[1])
          ? QUERY_KEY_PATTERNS[1]
          : ''
    })

    headerStore.updateShowStatus(props.hasHeader)

    return {
      // store
      screenLockStore,
      userStore,
      clientStore,

      // data
      sections,
      query,

      // computed
      ctaButtonLabel,
      trackingCodePrefix,
    }
  },
})
</script>

<template>
  <div
    ref="root"
    class="f-container"
    :data-app="clientStore.context.isApp"
    :data-screenlock="screenLockStore.isLock ? 'true' : 'false'"
    :data-backdrop-darken="screenLockStore.isBlackOut"
    :data-backdrop-blur="screenLockStore.isBlur">
    <div
      id="global-container"
      class="f-container--body">
      <AboutHeader
        v-if="!clientStore.context.isApp"
        :sections="sections"
        :tracking-code-prefix="trackingCodePrefix" />

      <section
        class="f-section__contents"
        :data-app="clientStore.context.isApp">
        <div class="f-section__contents--body is-hero">
          <SplitTestWrapper>
            <template #default>
              <AboutHero
                :cta-button-label="ctaButtonLabel"
                :tracking-code-prefix="trackingCodePrefix" />
            </template>
            <template #mail_campaign>
              <AboutHeroB
                :query="query"
                :cta-button-label="ctaButtonLabel"
                :tracking-code-prefix="trackingCodePrefix" />
            </template>
            <template #media>
              <AboutHeroB
                :query="query"
                :cta-button-label="ctaButtonLabel"
                :tracking-code-prefix="trackingCodePrefix" />
            </template>
          </SplitTestWrapper>
        <!-- /.f-section__contents body --></div>

        <div class="f-section__contents--body is-primary">
          <AboutMain
            :query="query"
            :cta-button-label="ctaButtonLabel"
            :tracking-code-prefix="trackingCodePrefix"
            @mounted:sections="sections = $event" />
        <!-- /.f-section__contents body --></div>
      <!-- /.f-section__contents --></section>

      <FooterLP v-if="!clientStore.context.isApp" />

      <!-- overlay contents -->
      <MediaQueryObserver />
    <!-- /.f-container body --></div>
  <!-- /.f-container --></div>
</template>

<style lang="sass" scoped>
$global-top-gutter-px: 20
$header-height-pc: 60
$header-height-sp: 42
.f-container
  $root: &
  width: 100%
  height: 100%
  min-height: 100%

  &::before
    content: ''
    +transition

  &[data-screenlock="true"]
    &::before
      width: 100%
      height: 100%
      display: block
      position: fixed
      top: 0
      left: 0
      z-index: 20

    &[data-backdrop-darken="true"]::before
      background-color: rgba($bg-black, 0.56)

    // スクリーンロック時に「ぼかし」を入れる場合
    &[data-backdrop-blur="true"]::before
      -webkit-backdrop-filter: blur(6px)
      backdrop-filter: blur(6px)

    &:not([data-backdrop-darken="true"])[data-backdrop-blur="true"]::before
      background-color: rgba($bg-white, 0.3)

  & &--body
    min-height: 100%
    display: flex
    flex-direction: column
    padding-top: #{$header-height-pc}px
    background-color: rgba($bg-x-light-gray, 1)

  +breakpoint($max-media: tablet)
    #{$root}--body
      padding-top: #{$header-height-sp}px

.f-section__contents
  $root: &
  width: 100%
  flex-grow: 1
  margin: 0 auto
</style>
